import React, { useMemo } from 'react'
import {
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Skeleton,
    Text,
    chakra,
    Center,
} from '@chakra-ui/react'
import { Bookmark, Eye, File, TrendingDown, TrendingUp } from '@emotion-icons/feather'
import { gql } from '@urql/core'
import { Company } from 'entities/company'
import { Job } from 'entities/job'
import { Me } from 'entities/user'
import { isServerSide } from 'lib/env'
import { formatAmountSigned } from 'lib/number'
import useQuery from 'hooks/useQuery'
import GetInTouchImage from 'public/img/get_in_touch.svg'

const BookmarkIcon = chakra(Bookmark)
const EyeIcon = chakra(Eye)
const FileIcon = chakra(File)
const TrendingDownIcon = chakra(TrendingDown)
const TrendingUpIcon = chakra(TrendingUp)
const GetInTouchImageChakra = chakra(GetInTouchImage)

type JobForStatsBox = Pick<Job, 'id' | 'companyId' | 'manager'>
type Stats = { count?: number; rate?: number }

type StatsBoxProps = {
    views?: Stats
    likes?: Stats
    applications?: Stats
}

const isJobOwner = (me: Me, job?: JobForStatsBox) => {
    return job?.companyId === me?.companyId
}

const QUERY_COMPANY_INFO_FOR_STATS_BOX = gql<
    { company: Pick<Company, 'manager'> },
    { id: string }
>`
    query query_company_info_for_stats_box($id: ID!) {
        company(id: $id) {
            id
            manager
        }
    }
`

const defaultEmail = 'hello@works-hub.com'

const GetInTouchPopUp = ({
    children,
    job,
    currentUser,
}: {
    children: React.ReactNode
    job?: JobForStatsBox
    currentUser: Me
}) => {
    const isOwner = isJobOwner(currentUser, job)

    const [result] = useQuery({
        query: QUERY_COMPANY_INFO_FOR_STATS_BOX,
        variables: { id: currentUser?.companyId! },
        pause: !isOwner || isServerSide || !job?.companyId,
    })

    const email = isOwner
        ? job?.manager || result.data?.company.manager || defaultEmail
        : defaultEmail

    return (
        <Popover trigger="hover" arrowSize={15}>
            <PopoverTrigger>{children}</PopoverTrigger>
            <PopoverContent
                bgColor="secondary.500"
                color="white"
                minWidth={{ base: '360px', md: '480px' }}
                borderRadius="12px"
            >
                <PopoverArrow bgColor="secondary.500" />
                <PopoverBody
                    px={{ base: '24px', md: '10px' }}
                    py={{ base: '28px', md: '19px' }}
                >
                    <Flex direction={{ base: 'row-reverse', md: 'row' }} gridGap="20px">
                        <Box display={{ base: 'none', md: 'unset' }}>
                            <GetInTouchImageChakra />
                        </Box>

                        <Flex
                            direction="column"
                            justifyContent="center"
                            gridGap={{ base: '0px', md: '20px' }}
                        >
                            <Flex justifyContent="space-between">
                                <Text
                                    variant="bodyTextMedium"
                                    color="white"
                                    fontWeight="600"
                                    lineHeight="26px"
                                >
                                    Get in touch to&nbsp;discuss your hiring&nbsp;needs
                                </Text>
                                <Box mt="-15px">
                                    <GetInTouchImageChakra
                                        boxSize="105px"
                                        display={{ md: 'none', base: 'unset' }}
                                    />
                                </Box>
                            </Flex>
                            <Flex>
                                <Button
                                    mr="8px"
                                    variant="tertiary"
                                    title="Email us"
                                    aria-label="Email us"
                                    as="a"
                                    href={`mailto:${email}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Email us
                                </Button>
                                <Button
                                    variant="borderOnly"
                                    colorScheme="gray.bg_light"
                                    color="white"
                                    bgColor="transparent"
                                    _hover={{
                                        backgroundColor: 'gray.dividers',
                                        color: 'text.500',
                                    }}
                                    aria-label="Book a call"
                                    title="Book a call"
                                    as="a"
                                    href="https://meetings.hubspot.com/workshub/take-off-demo"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Book a call
                                </Button>
                            </Flex>
                        </Flex>
                    </Flex>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

const StatsFlair = ({ value }: { value: number }) => {
    const isUp = value >= 0
    const color = `semantic.${isUp ? 'success' : 'error'}.accent`
    const bgColor = `semantic.${isUp ? 'success' : 'error'}.bg`

    return (
        <Flex
            w="fit-content"
            p="2px 6px"
            gridGap="7px"
            alignItems="center"
            justifyContent="flex-end"
            borderRadius="6px"
            color={color}
            backgroundColor={bgColor}
        >
            {isUp ? (
                <TrendingUpIcon boxSize="16px" strokeWidth={1.5} />
            ) : (
                <TrendingDownIcon boxSize="16px" strokeWidth={1.5} />
            )}
            <Text fontWeight={500} fontSize="16px" lineHeight="20px" color={color}>
                {`${formatAmountSigned(value)}%`}
            </Text>
        </Flex>
    )
}

const StatsRow = ({
    icon,
    title,
    count,
    rate,
}: {
    icon: any
    title: string
} & Stats) => {
    const isLoaded = count !== undefined && rate !== undefined

    return (
        <>
            <Flex alignItems="center">
                <Flex alignItems="center" gridGap="12px">
                    <Flex w="24px" justifyContent="center">
                        {icon}
                    </Flex>
                    <Text variant="bodyTextSmall" fontWeight={500}>
                        {title}
                    </Text>
                </Flex>
            </Flex>
            <Skeleton isLoaded={isLoaded}>
                <Flex justifyContent="flex-start">
                    <Text variant="bodyTextLarge" lineHeight="24px">
                        {count}
                    </Text>
                </Flex>
            </Skeleton>
            <Skeleton isLoaded={isLoaded}>
                {rate !== undefined && (
                    <Flex textAlign="right" justifyContent="flex-end">
                        <StatsFlair value={rate} />
                    </Flex>
                )}
            </Skeleton>
        </>
    )
}

export const StatsBox = ({
    views,
    likes,
    applications,
    currentUser,
    job,
}: StatsBoxProps & { currentUser: Me; job?: JobForStatsBox }) => {
    return (
        <Flex
            direction="column"
            gridGap="18px"
            p="26px 24px"
            width="100%"
            borderRadius="12px"
            backgroundColor="white"
        >
            <Heading size="sm">Last {DAYS_IN_MONTH} days’ stats</Heading>
            {!isJobOwner(currentUser, job) ? (
                <>
                    <Center py={'24px'}>
                        <Text variant="bodyTextSmall" color="text.500" align={'center'}>
                            Statistics are available only for the jobs you own
                        </Text>
                    </Center>
                    <GetInTouchPopUp job={job} currentUser={currentUser}>
                        <Text
                            variant="bodyTextSmall"
                            color="secondary.text"
                            textDecoration="underline"
                        >
                            Want to get better results?
                        </Text>
                    </GetInTouchPopUp>
                </>
            ) : (
                <>
                    <Grid
                        gridTemplateColumns="repeat(3, auto)"
                        gridRowGap="16px"
                        gridColumnGap="6px"
                        mb="4px"
                    >
                        <StatsRow
                            title="Viewed"
                            icon={<EyeIcon boxSize="24px" strokeWidth={1.5} />}
                            {...views}
                        />
                        <StatsRow
                            title="Applied"
                            icon={<FileIcon boxSize="21px" strokeWidth={1.5} />}
                            {...applications}
                        />
                        <StatsRow
                            title="Saved"
                            icon={<BookmarkIcon boxSize="24px" strokeWidth={1.5} />}
                            {...likes}
                        />
                    </Grid>
                    <GetInTouchPopUp job={job} currentUser={currentUser}>
                        <Text
                            variant="bodyTextSmall"
                            color="secondary.text"
                            textDecoration="underline"
                        >
                            Want to get better results?
                        </Text>
                    </GetInTouchPopUp>
                </>
            )}
        </Flex>
    )
}

const DAYS_IN_MONTH = 30
const TWO_MONTH = 2

type DataPoint = { date: string; count: number }

export const QUERY_JOB_STATS = gql<
    {
        job_analytics: {
            applications: [DataPoint, DataPoint]
            views: [DataPoint, DataPoint]
            likes: [DataPoint, DataPoint]
        }
    },
    { companyId: string; granularity: number; num_periods: number; jobId: string }
>`
    query job_stats(
        $companyId: ID!
        $jobId: String
        $granularity: Int!
        $num_periods: Int!
    ) {
        job_analytics(
            company_id: $companyId
            job_id: $jobId
            granularity: $granularity
            num_periods: $num_periods
        ) {
            applications {
                date
                count
            }
            views {
                date
                count
            }
            likes {
                date
                count
            }
        }
    }
`

const convertDataPointsToStats = (points: [DataPoint, DataPoint]): Stats => {
    return {
        // aggregate for [-30d, now]
        count: points[1].count,
        // rate of change between two aggregates: [-60d, -30d] and [-30d, now]
        rate: Math.floor(
            ((points[1].count - points[0].count) / (points[0].count || 1)) * 100,
        ),
    }
}

export const StatsBoxWithData = ({
    job,
    currentUser,
}: {
    job?: JobForStatsBox
    currentUser: Me
}) => {
    const showStatsBox = currentUser?.isAdmin || currentUser?.isCompanyUser

    const [result] = useQuery({
        query: QUERY_JOB_STATS,
        variables: {
            companyId: job?.companyId!,
            granularity: DAYS_IN_MONTH,
            num_periods: TWO_MONTH,
            jobId: job?.id!,
        },
        pause: isServerSide || !job || !showStatsBox || !isJobOwner(currentUser, job),
    })

    const companyStats = useMemo(() => {
        if (result.data?.job_analytics) {
            const { applications, views, likes } = result.data.job_analytics
            return {
                applications: convertDataPointsToStats(applications),
                views: convertDataPointsToStats(views),
                likes: convertDataPointsToStats(likes),
            }
        }
    }, [result.data])

    if (!showStatsBox) {
        return null
    }

    return (
        <StatsBox
            views={companyStats?.views}
            likes={companyStats?.likes}
            applications={companyStats?.applications}
            currentUser={currentUser}
            job={job}
        />
    )
}
